import { IFrameInfo } from "../model/IFrameInfo";
import { Looping, PlayDirection } from "./Looping";
import { GlobalEventBus } from "../../events/GlobalEventBus";
import { EventTypes } from "../../events/EventTypes";
import { BoundingBoxScan } from "../../../workers/BoundingBoxScanner";
import { IBoundingBox } from "../model/IBoundingBox";

export class AlphaVideoPlayer {
    public currentTime: number = -1;
    public videoSrc!: string;
    public playMode: number = Looping.PINGPONG;
    public fps: number = 15;
    public currentFrame: number = 0;
    public totalFrames: number = 0;
    public rendered: boolean = false;
    public videoWidth: number = 0;
    public videoHeight: number = 0;
    public playing: Boolean = true;
    private videoProcessWorker: Worker | undefined;
    private video!: HTMLVideoElement;
    private videoCanvas!: HTMLCanvasElement;
    private videoContext!: CanvasRenderingContext2D;
    private canvasToPlayOn: HTMLCanvasElement;
    private readonly offscreen: boolean = false;
    private frameInfo: IFrameInfo[] = [];
    private playDirection: number = PlayDirection.FORWARD;
    private boundingBoxScanner: BoundingBoxScan = new BoundingBoxScan();
    private videoInterval: any;
    private lastBB: IBoundingBox | undefined;
    private bbcount: number = 0;


    constructor(canvasToPlayOn: HTMLCanvasElement) {
        this.canvasToPlayOn = canvasToPlayOn;
        if ("OffscreenCanvas" in window) {
            this.videoProcessWorker = new Worker(new URL("../../../workers/AlphaVideoProcessWorker.ts", import.meta.url));
            this.offscreen = false;
        }
        console.log("Alpha video player started");
    }

    async loadFiles(videoSrc: string): Promise<void> {
        this.clear();
        GlobalEventBus.dispatchEvent({
            type: EventTypes.BAG_VIDEO_LOADING,
            eventData: this.videoSrc
        });
        console.log("loadFiles", videoSrc);
        const jpegSrc = videoSrc + '.FRAME.jpg';
        const frameImage = new Image();
        frameImage.src = jpegSrc;
        if(this.videoContext){
            this.videoContext.clearRect(0,0,this.videoCanvas.width, this.videoCanvas.height);
        }
        frameImage.onload = async () => {
            console.log('Frame image loaded')
            if(!this.videoCanvas){
                this.videoCanvas = document.createElement('canvas');
            }
            this.videoCanvas.width = frameImage.width;
            this.videoCanvas.height = frameImage.height;
            this.videoContext = this.videoCanvas.getContext('2d')!;
            await this.processFrame(frameImage);
            if(this.video){
                this.video.currentTime = 0;
                this.video.src = '';
            }
            await this.handleVideoFrameLoaded();
            console.log('Frame processed, now loading video...')
            GlobalEventBus.dispatchEvent({
                type: EventTypes.BAG_VIDEO_LOADED,
                eventData: this.videoSrc
            });
            this.video = await this.loadVideo(videoSrc);
            console.log(videoSrc, "loaded");
            this.start();
        }
        this.videoSrc = videoSrc;


    }

    stop() {
        console.log("Stopping video", this.videoSrc);
        this.playing = false;
    }

    clear() {
        this.stop();
        this.rendered = false;
        this.frameInfo = [];
        this.currentFrame = 0;
        this.currentTime = 0;
        this.bbcount = 0;
        if (this.video){
            this.video.onseeking = null;
            this.video.onseeked = null;
            this.video.onloadeddata = null;
            this.video.onerror = null;
            this.video.pause();
        }
        if (this.videoInterval){
            clearInterval(this.videoInterval);
        }
    }

    start() {
        console.log("Starting video", this.videoSrc);
        this.playing = true;
    }

    private async seekVideoToFrame(frame: number){
        console.log('Seeking to ', frame);
        return new Promise<void>((resolve, reject) => {
            const handleSeeked = () => {
                console.log('Seeked')
                this.video.onseeked = handleSeeked;
                resolve();
            }
            const handleSeeking = () => {
                console.log('Seeking')
                this.video.onseeking = null;
                this.video.onseeked = handleSeeked;
            }
            this.video.onseeking = handleSeeking;
            this.video.currentTime = frame / this.fps;
        });
    }

    public async seekToFrame (frame: number) {
        if(!this.rendered) {
            if(Math.abs(this.video.currentTime - (this.currentFrame / this.fps)) > 0.1) {
                await this.seekVideoToFrame(frame);
                this.currentFrame = frame;
            }
        } else {
            this.currentFrame = frame;
        }
    }

    private checkRendered(): boolean{
        if (this.rendered){
            return true;
        }
        let rendered = true;
        for (let i=1; i<this.totalFrames; i++){
            if(!this.frameInfo[i]?.imageData){
                rendered = false;
            }
        }
        this.rendered = rendered;
        return rendered;
    }

    private async handleVideoFrameLoaded() {
        const rendered = this.checkRendered();
        if (!rendered) {
            this.currentFrame = Math.floor(this.video.currentTime * this.fps);
        }
        let frameInfo = this.frameInfo[this.currentFrame];
        if (!frameInfo?.imageData) {
            await this.processFrame();
            frameInfo = this.frameInfo[this.currentFrame];
        }
        if (this.playing && frameInfo.boundingBox.right !== 0) {
            if (frameInfo.emptyFrame) {
                console.log('Empty frame detected!!!')
            } else {
                this.canvasToPlayOn.getContext("2d")!.putImageData(frameInfo.imageData, 0, 0);
                GlobalEventBus.dispatchEvent({
                    type: EventTypes.VIDEO_BOUNDING_BOX_CHANGE,
                    eventData: frameInfo.boundingBox
                });
            }
        }
        if (this.playDirection === PlayDirection.FORWARD) {
            this.currentFrame++;
        } else {
            this.currentFrame--;
        }
        if (this.currentFrame >= this.totalFrames) {
            if(!this.rendered) {
                console.log('Restarting');
                this.video.currentTime = 0;
                this.video.play();
            }
        }
        if (this.playMode === Looping.LOOP) {
            if (this.currentFrame >= this.totalFrames) {
                this.currentFrame = 0;
            }
        } else if (this.playMode === Looping.PINGPONG) {
            if (this.playDirection === PlayDirection.FORWARD) {
                if (this.currentFrame >= this.totalFrames) {
                    this.currentFrame = this.totalFrames - 1;
                    this.playDirection = PlayDirection.BACKWARD;
                }
            } else {
                if (this.currentFrame < 0) {
                    this.currentFrame = 1;
                    this.playDirection = PlayDirection.FORWARD;
                }
            }
        }
        if (this.rendered && this.video?.src?.length > 0) {
            console.log("Rendered!!!!!!!!!!!");
            this.video.pause();
            this.video.removeAttribute("src"); // empty source
        }
    }

    private async processFrame(img?: HTMLImageElement): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.bbcount ++;
            let width: number, height: number;
            let currentTime: number, currentFrame: number;
            if (img) {
                this.bbcount = 1;
                this.currentFrame = 0;
                width = img.width;
                height = img.height;
                currentTime = 0;
                currentFrame = 0;
                this.canvasToPlayOn.width = width;
                this.canvasToPlayOn.height = height / 2;
                this.videoContext.drawImage(img, 0, 0, width, height);
                this.videoWidth = width;
                this.videoHeight = height;
            } else {
                width = this.videoCanvas.width;
                height = this.videoCanvas.height;
                currentFrame = this.currentFrame;
                currentTime = this.video.currentTime;
                this.videoContext.drawImage(this.video, 0, 0, width, height);
            }
            const image = this.videoContext.getImageData(0, 0, width, height / 2);
            const imageData = image.data;
            const alphaData = this.videoContext.getImageData(0, height / 2, width, height / 2).data;
            let i = 3;
            const len = imageData.length;
            for (; i < len; i = i + 4) {
                imageData[i] = alphaData[i - 1];
            }

            let bb;
            const alphaBuffer = new Uint8ClampedArray(alphaData.buffer);
            if (this.bbcount > 4 || this.bbcount === 1) {
                bb = this.boundingBoxScanner.scan(alphaBuffer, width, height / 2);
                this.lastBB = bb;
                if (this.bbcount > 4) {
                    this.bbcount = 0;
                }
            } else {
                bb = this.lastBB;
            }
            let emptyFrame = false;
            if (img) {
                GlobalEventBus.dispatchEvent({
                    type: EventTypes.VIDEO_BOUNDING_BOX_CHANGE,
                    eventData: bb
                });
            } else {
                emptyFrame = this.boundingBoxScanner.fastScanForTransparentFrame(alphaBuffer, width, height/2);
            }
            this.frameInfo[currentFrame] = {
                imageData: image,
                time: currentTime,
                boundingBox: bb as IBoundingBox,
                emptyFrame
            }
            resolve();

        });
    }

    private createCanvasForVideo(video: HTMLVideoElement): HTMLCanvasElement {
        const canvas: HTMLCanvasElement = document.createElement("canvas");
        const { videoWidth, videoHeight } = video;
        canvas.width = videoWidth;
        canvas.height = videoHeight;
        return canvas;
    }

    private startInterval(){
        this.videoInterval = setInterval(this.handleVideoFrameLoaded.bind(this), 1000 / this.fps);
    }

    private clearInterval(){
        clearInterval(this.videoInterval);
    }

    private async loadVideo(src: string): Promise<HTMLVideoElement> {
        this.clear();
        return new Promise<HTMLVideoElement>(async (resolve, reject) => {
            const videoURL = src;
            const video = document.createElement("video");
            video.preload = "auto";
            video.crossOrigin = "Anonymous";
            this.video = video;
            video.onloadedmetadata = async () => {
                console.log('onloadedmetadata');
                if(this.videoInterval){
                    clearInterval(this.videoInterval);
                }
                this.videoWidth = this.video.videoWidth;
                this.videoHeight = this.video.videoHeight;
                this.totalFrames = Math.floor(this.video.duration * this.fps) -1;
                this.video.play();
                this.videoInterval = setInterval(this.handleVideoFrameLoaded.bind(this), 1000 / this.fps);
                resolve(video);
            };
            video.src = videoURL;
            video.muted = true;
            video.autoplay = true;
            video.playsInline = true;
            video.onerror = (e) => {
                reject(e);
            };
            this.currentTime = 0;
        });
    }
}
